import { memo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Checkbox,
    Collapse,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add, Delete, DragIndicator, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { FormFieldDto, FormFieldItemType, FormFieldType, OptionMode } from '../document-generation.dto';
import { useAppSelector } from 'src/redux-app-hooks';

interface SectionFieldProps {
    field: FormFieldDto;
    fieldIndex: number;
    onUpdateField: (index: number, field: FormFieldDto) => void;
    onRemoveField: (field: FormFieldDto) => void;
    onAddOption: (fieldKey: string, option: string) => void;
    onRemoveOption: (fieldKey: string, optionKey: string) => void;
}

export function SortableSectionField(props: SectionFieldProps) {
    const { field } = props;
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: field._id ?? field.key });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative' as const,
    };

    return (
        <Grid size={12} sx={style} ref={setNodeRef}>
            <Box
                {...attributes}
                {...listeners}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: -30,
                    zIndex: 10,
                    cursor: 'grab',
                    ':active': { cursor: 'grabbing' },
                }}
            >
                <DragIndicator fontSize="medium" />
            </Box>
            <SectionField {...props} />
        </Grid>
    );
}

export const SectionField = memo(
    ({ field, fieldIndex, onUpdateField, onRemoveField, onAddOption, onRemoveOption }: SectionFieldProps) => {
        const { t } = useTranslation();
        const dataCatalog = useAppSelector((state) => state.documentGenerationTemplate.dataCatalog);
        const [expanded, setExpanded] = useState(false);

        const updateField = (updates: Partial<FormFieldDto>) => {
            onUpdateField(fieldIndex, { ...field, ...updates });
        };

        return (
            <Grid
                size={12}
                spacing={2}
                container
                direction="row"
                sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#f3f3f3ff',
                    padding: 2,
                    borderRadius: 1,
                }}
            >
                <Grid size={10.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6">{field.key || `${t('Field')} #${fieldIndex + 1}`}</Typography>
                        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                            {expanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>
                </Grid>
                <Grid size={1.5}>
                    <IconButton aria-label={t('Remove field')} onClick={() => onRemoveField(field)} color="error">
                        <Delete />
                    </IconButton>
                </Grid>

                <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                    <Grid size={12} container spacing={2}>
                        <Grid size={12} direction="row" container>
                            <Grid size={6}>
                                <Autocomplete
                                    freeSolo
                                    fullWidth
                                    options={dataCatalog.map((item) => ({ label: item.key, value: item.key }))}
                                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                                    value={field.key}
                                    onInputChange={(_, newInputValue) => {
                                        updateField({ key: newInputValue });
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('Key')} variant="standard" size="small" />
                                    )}
                                />
                            </Grid>

                            <Grid size={6}>
                                <TextField
                                    required
                                    fullWidth
                                    label={t('Label')}
                                    type="text"
                                    variant="standard"
                                    size="small"
                                    placeholder={t('Label')}
                                    value={field.label}
                                    onChange={(e) => updateField({ label: e.target.value })}
                                />
                            </Grid>
                        </Grid>

                        <Grid size={12} direction="row" container>
                            <Grid size={6}>
                                <Select
                                    label={t('Type')}
                                    value={field.type}
                                    fullWidth
                                    variant="standard"
                                    onChange={(e) => updateField({ type: e.target.value as FormFieldType })}
                                    input={<OutlinedInput label={t('Type')} size="small" notched />}
                                >
                                    {Object.values(FormFieldType).map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {t(type)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>

                            {field.type === FormFieldType.LIST && (
                                <Grid size={6}>
                                    <Select
                                        label={t('Item type')}
                                        value={field.typeItem ?? ''}
                                        fullWidth
                                        variant="standard"
                                        onChange={(e) => updateField({ typeItem: e.target.value as FormFieldItemType })}
                                        input={<OutlinedInput label={t('Item Type')} size="small" notched />}
                                    >
                                        {Object.values(FormFieldItemType).map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {t(type)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                            )}
                        </Grid>

                        {field.type === FormFieldType.DROPDOWN && (
                            <Grid size={12} direction="row" container>
                                <Grid size={3}>
                                    <Select
                                        fullWidth
                                        label={t('Options Mode')}
                                        value={field.config.optionsMode ?? ''}
                                        displayEmpty
                                        size="small"
                                        onChange={(e) => {
                                            const mode = e.target.value as OptionMode;
                                            updateField({
                                                config: {
                                                    ...field.config,
                                                    optionsMode: mode,
                                                    options: mode === 'STATIC' ? [''] : [],
                                                    dynamicOptionsKey: mode === 'DYNAMIC' ? '' : null,
                                                },
                                            });
                                        }}
                                    >
                                        <MenuItem value="STATIC">{t('Static Options')}</MenuItem>
                                        <MenuItem value="DYNAMIC">{t('Dynamic Options')}</MenuItem>
                                    </Select>
                                </Grid>
                                {field.config.optionsMode === 'STATIC' && (
                                    <Grid size={12}>
                                        <Grid size={12} direction="row" container>
                                            <Grid size={6} direction="row" container>
                                                <Grid size={11.5}>
                                                    <Typography variant="subtitle1">{t('Options')}</Typography>
                                                </Grid>
                                                <Grid size={0.5}>
                                                    <IconButton
                                                        aria-label={t('Add option')}
                                                        onClick={() =>
                                                            onAddOption(
                                                                field.key,
                                                                `Option ${field.config.options!.length + 1}`,
                                                            )
                                                        }
                                                    >
                                                        <Add />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid size={6} direction="row" container>
                                            {field.config.options!.map((option, optionIndex) => (
                                                <Grid size={12} direction="row" container key={optionIndex}>
                                                    <Grid size={11.5}>
                                                        <TextField
                                                            fullWidth
                                                            label={`${t('Option')} #${optionIndex + 1}`}
                                                            type="text"
                                                            variant="standard"
                                                            size="small"
                                                            placeholder={`${t('Option')} #${optionIndex + 1}`}
                                                            value={option}
                                                            onChange={(e) =>
                                                                updateField({
                                                                    config: {
                                                                        ...field.config,
                                                                        options: field.config.options!.map(
                                                                            (opt, idx) =>
                                                                                idx === optionIndex
                                                                                    ? e.target.value
                                                                                    : opt,
                                                                        ),
                                                                    },
                                                                })
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid size={0.5}>
                                                        <IconButton
                                                            aria-label={t('Remove option')}
                                                            onClick={() => onRemoveOption(field.key, option)}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                )}
                                {field.config.optionsMode === 'DYNAMIC' && (
                                    <Grid size={6}>
                                        <Autocomplete
                                            freeSolo
                                            fullWidth
                                            options={dataCatalog.map((item) => ({
                                                label: item.key,
                                                value: item.key,
                                            }))}
                                            getOptionLabel={(option) =>
                                                typeof option === 'string' ? option : option.label
                                            }
                                            value={field.config.dynamicOptionsKey || ''}
                                            onInputChange={(_, newInputValue) => {
                                                updateField({
                                                    config: {
                                                        ...field.config,
                                                        dynamicOptionsKey: newInputValue || null,
                                                    },
                                                });
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('Dynamic Options Key')}
                                                    variant="standard"
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        )}

                        <Grid size={12} direction="row" container sx={{ mt: 1 }}>
                            <Typography variant="subtitle1">{t('Config')}</Typography>
                        </Grid>

                        <Grid size={12} direction="row" container>
                            <Grid size={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="required"
                                            size="small"
                                            checked={field.config.required}
                                            onChange={(e) =>
                                                updateField({
                                                    config: {
                                                        ...field.config,
                                                        required: e.target.checked,
                                                    },
                                                })
                                            }
                                        />
                                    }
                                    label={t('Required')}
                                />
                            </Grid>

                            <Grid size={6}>
                                <TextField
                                    fullWidth
                                    label={t('Default Value')}
                                    type="text"
                                    variant="standard"
                                    size="small"
                                    placeholder={t('Default Value')}
                                    value={field.config.defaultValue || ''}
                                    onChange={(e) =>
                                        updateField({
                                            config: {
                                                ...field.config,
                                                defaultValue: e.target.value,
                                            },
                                        })
                                    }
                                />
                            </Grid>
                        </Grid>

                        <Grid size={12} direction="row" container>
                            <Grid size={6}>
                                <TextField
                                    fullWidth
                                    label={t('Placeholder')}
                                    type="text"
                                    variant="standard"
                                    size="small"
                                    placeholder={t('Placeholder')}
                                    value={field.config.placeholder || ''}
                                    onChange={(e) =>
                                        updateField({
                                            config: {
                                                ...field.config,
                                                placeholder: e.target.value,
                                            },
                                        })
                                    }
                                />
                            </Grid>
                        </Grid>

                        <Grid size={12} direction="row" container sx={{ mt: 1 }}>
                            <Typography variant="subtitle1">{t('Validation')}</Typography>
                        </Grid>
                        <Grid size={12} direction="row" container>
                            {field.type === FormFieldType.TEXT && (
                                <>
                                    <Grid size={3}>
                                        <TextField
                                            fullWidth
                                            label={t('Min Length')}
                                            type="number"
                                            variant="standard"
                                            size="small"
                                            placeholder={t('Min Length')}
                                            value={field.config.validation?.minLength || ''}
                                            onChange={(e) =>
                                                updateField({
                                                    config: {
                                                        ...field.config,
                                                        validation: {
                                                            ...field.config.validation,
                                                            minLength: e.target.value
                                                                ? parseInt(e.target.value, 10)
                                                                : null,
                                                            maxLength: field.config.validation?.maxLength ?? null,
                                                            minValue: field.config.validation?.minValue ?? null,
                                                            maxValue: field.config.validation?.maxValue ?? null,
                                                            pattern: field.config.validation?.pattern ?? null,
                                                        },
                                                    },
                                                })
                                            }
                                        />
                                    </Grid>

                                    <Grid size={3}>
                                        <TextField
                                            fullWidth
                                            label={t('Max Length')}
                                            type="number"
                                            variant="standard"
                                            size="small"
                                            placeholder={t('Max Length')}
                                            value={field.config.validation?.maxLength || ''}
                                            onChange={(e) =>
                                                updateField({
                                                    config: {
                                                        ...field.config,
                                                        validation: {
                                                            minLength: field.config.validation?.minLength ?? null,
                                                            maxLength: e.target.value
                                                                ? parseInt(e.target.value, 10)
                                                                : null,
                                                            minValue: field.config.validation?.minValue ?? null,
                                                            maxValue: field.config.validation?.maxValue ?? null,
                                                            pattern: field.config.validation?.pattern ?? null,
                                                        },
                                                    },
                                                })
                                            }
                                        />
                                    </Grid>

                                    <Grid size={6}>
                                        <TextField
                                            fullWidth
                                            label={t('Pattern')}
                                            type="text"
                                            variant="standard"
                                            size="small"
                                            placeholder={t('Pattern')}
                                            value={field.config.validation?.pattern || ''}
                                            onChange={(e) =>
                                                updateField({
                                                    config: {
                                                        ...field.config,
                                                        validation: {
                                                            minLength: field.config.validation?.minLength ?? null,
                                                            maxLength: field.config.validation?.maxLength ?? null,
                                                            minValue: field.config.validation?.minValue ?? null,
                                                            maxValue: field.config.validation?.maxValue ?? null,
                                                            pattern: e.target.value || null,
                                                        },
                                                    },
                                                })
                                            }
                                        />
                                    </Grid>
                                </>
                            )}

                            {field.type === FormFieldType.NUMBER && (
                                <>
                                    <Grid size={3}>
                                        <TextField
                                            fullWidth
                                            label={t('Min Value')}
                                            type="number"
                                            variant="standard"
                                            size="small"
                                            placeholder={t('Min Value')}
                                            value={field.config.validation?.minValue || ''}
                                            onChange={(e) =>
                                                updateField({
                                                    config: {
                                                        ...field.config,
                                                        validation: {
                                                            minLength: field.config.validation?.minLength ?? null,
                                                            maxLength: field.config.validation?.maxLength ?? null,
                                                            minValue: e.target.value
                                                                ? parseInt(e.target.value, 10)
                                                                : null,
                                                            maxValue: field.config.validation?.maxValue ?? null,
                                                            pattern: field.config.validation?.pattern ?? null,
                                                        },
                                                    },
                                                })
                                            }
                                        />
                                    </Grid>

                                    <Grid size={3}>
                                        <TextField
                                            fullWidth
                                            label={t('Max Value')}
                                            type="number"
                                            variant="standard"
                                            size="small"
                                            placeholder={t('Max Value')}
                                            value={field.config.validation?.maxValue || ''}
                                            onChange={(e) =>
                                                updateField({
                                                    config: {
                                                        ...field.config,
                                                        validation: {
                                                            minLength: field.config.validation?.minLength ?? null,
                                                            maxLength: field.config.validation?.maxLength ?? null,
                                                            minValue: field.config.validation?.minValue ?? null,
                                                            maxValue: e.target.value
                                                                ? parseInt(e.target.value, 10)
                                                                : null,
                                                            pattern: field.config.validation?.pattern ?? null,
                                                        },
                                                    },
                                                })
                                            }
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Collapse>
            </Grid>
        );
    },
);
